import type { BuilderState } from "../builder-state";
import { isSafeIdent, NO_CAPS, type ConnectionString, type DialectCapabilities } from "./shared";

export const label = "IBM Db2";
export const defaultPort = 50000;

export const caps: DialectCapabilities = {
  ...NO_CAPS,
  schemaToggle: true,
  truncatePriv: true,
  referencesAndTrigger: true,
  quoteIdentifiers: true,
  superuserToggle: true, // DBADM
  connectionLimit: true,
  validUntil: true,
};

export function generate(s: BuilderState): string {
  const out: string[] = [];
  const push = (l = "") => out.push(l);
  const c = (t: string) => s.includeComments && push(`-- ${t}`);

  // Db2 identifiers are upper-cased unless double-quoted.
  const ident = (v: string) =>
    s.quoteIdentifiers || !isSafeIdent(v) ? `"${v.replace(/"/g, '""')}"` : v.toUpperCase();
  const db = (s.dbName || "APPDB").toUpperCase();
  const user = ident(s.userName);
  const schema = ident(s.schemaName);

  c("============================================================");
  c(`IBM Db2 — ${s.mode === "create" ? "Create" : "Update"} database, schema & grants`);
  c("Db2 authenticates against the OS / LDAP — it has no CREATE USER.");
  c("Create the OS account first (e.g. useradd), then run this script as");
  c("an instance owner / SECADM.");
  c("============================================================");
  push();

  if (s.mode === "create") {
    c("---- 1. DATABASE (CLP) --------------------------------------");
    c("Run the next two lines from the Db2 command-line processor (db2):");
    push(`-- db2 "CREATE DATABASE ${db} USING CODESET UTF-8 TERRITORY US"`);
    push(`-- db2 "CONNECT TO ${db}"`);
    push();
  } else {
    push(`-- db2 "CONNECT TO ${db}"`);
    push();
  }

  if (s.createSchema) {
    c("---- 2. SCHEMA ----------------------------------------------");
    push(`CREATE SCHEMA ${schema} AUTHORIZATION ${user};`);
    push();
  }

  c("---- 3. DATABASE-LEVEL AUTHORITIES --------------------------");
  push(`GRANT CONNECT ON DATABASE TO USER ${user};`);
  if (s.privCreateSchema) push(`GRANT IMPLICIT_SCHEMA ON DATABASE TO USER ${user};`);
  if (s.createDb) push(`GRANT CREATETAB ON DATABASE TO USER ${user};`);
  if (s.createRole) push(`GRANT SECADM ON DATABASE TO USER ${user};`);
  if (s.superuser) {
    c("DBADM bypasses object-level checks. Avoid for application accounts.");
    push(`GRANT DBADM ON DATABASE TO USER ${user};`);
  }
  push();

  if (s.createSchema) {
    c("---- 4. SCHEMA PRIVILEGES -----------------------------------");
    // Db2 has no USAGE on schema; CREATE -> CREATEIN.
    const grants: string[] = [];
    if (s.privCreateSchema) grants.push("CREATEIN", "ALTERIN", "DROPIN");
    if (grants.length) push(`GRANT ${grants.join(", ")} ON SCHEMA ${schema} TO USER ${user};`);
    push();
  }

  c("---- 5. OBJECT PRIVILEGES (template) ------------------------");
  c("Db2 grants are per-object. Replace SCHEMA.OBJECT with real targets.");
  const tableP: string[] = [];
  if (s.privSelect) tableP.push("SELECT");
  if (s.privInsert) tableP.push("INSERT");
  if (s.privUpdate) tableP.push("UPDATE");
  if (s.privDelete) tableP.push("DELETE");
  if (s.privReferences) tableP.push("REFERENCES");
  if (s.privTrigger) tableP.push("ALTER");
  if (s.privTruncate && !tableP.includes("ALTER")) tableP.push("ALTER");

  if (tableP.length) {
    push(`-- GRANT ${tableP.join(", ")} ON TABLE ${schema}.TABLE_NAME TO USER ${user};`);
  }
  if (s.privExecuteFn)
    push(`-- GRANT EXECUTE ON FUNCTION ${schema}.FUNCTION_NAME TO USER ${user};`);
  push();

  if (s.mode === "update" && s.revokeBeforeGrant) {
    c("Revoke template (mirror of GRANT). Adjust per-object as needed.");
    if (tableP.length)
      push(`-- REVOKE ${tableP.join(", ")} ON TABLE ${schema}.TABLE_NAME FROM USER ${user};`);
    push();
  }

  if (s.connectionLimit !== -1) {
    c(`Connection limits in Db2 are enforced via WLM thresholds (${s.connectionLimit}).`);
    push();
  }
  if (s.validUntil) {
    c(`Password expiry is managed by the OS / security plugin (target: ${s.validUntil}).`);
    push();
  }

  return out.join("\n");
}

export function connectionStrings(s: BuilderState): ConnectionString[] {
  const pwd = encodeURIComponent(s.password);
  const user = encodeURIComponent(s.userName);
  const dbSeg = encodeURIComponent(s.dbName || "APPDB");
  const auth = pwd ? `${user}:${pwd}` : user;
  return [
    {
      label: "CLI / db2cli",
      url: `DATABASE=${s.dbName || "APPDB"};HOSTNAME=${s.host};PORT=${s.port};PROTOCOL=TCPIP;UID=${s.userName};PWD=${s.password};`,
    },
    {
      label: "JDBC (Type 4)",
      url: `jdbc:db2://${s.host}:${s.port}/${dbSeg}:user=${user};password=${pwd};sslConnection=true;`,
    },
    {
      label: "SQLAlchemy (ibm_db_sa)",
      url: `db2+ibm_db://${auth}@${s.host}:${s.port}/${dbSeg}`,
    },
    {
      label: "Node.js (ibm_db)",
      url: `DATABASE=${s.dbName || "APPDB"};HOSTNAME=${s.host};PORT=${s.port};PROTOCOL=TCPIP;UID=${s.userName};PWD=${s.password};`,
      note: "Pass as connection string to ibm_db.open().",
    },
  ];
}
